import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Briefcase, Clock, Award, UploadCloud, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Loader } from "../components/ui-bits";

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "" });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setJob(null);
    api.get(`/jobs/${slug}`).then((r) => setJob(r.data)).catch(() => setJob(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!resume) { toast.error("Please attach your resume."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("job_slug", job.slug);
      fd.append("job_title", job.title);
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("cover_letter", form.cover_letter);
      fd.append("resume", resume);
      await api.post("/careers/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (job === null) return <Loader label="Loading position" />;
  if (job === false) return <div className="max-w-3xl mx-auto px-6 py-40 text-center text-slate-400" data-testid="job-notfound">Position not found. <Link to="/careers" className="text-emerald-400">Back to Careers</Link></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="job-detail-page">
      <Link to="/careers" data-testid="job-back-link" className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-400 text-sm font-mono mb-8">
        <ArrowLeft className="w-4 h-4" /> All Openings
      </Link>
      <p className="eyebrow mb-3">{job.department}</p>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold text-white tracking-tight leading-tight">{job.title}</h1>
      <div className="flex flex-wrap gap-4 mt-5 text-sm text-slate-400">
        <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-emerald-400" /> {job.location}</span>
        <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-emerald-400" /> {job.type}</span>
        <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-emerald-400" /> Posted {job.posted_date}</span>
        <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-emerald-400" /> {job.experience}</span>
      </div>

      <div className="prose-emerald max-w-none mt-10">
        {(job.description || []).map((p, i) => <p key={i}>{p}</p>)}
      </div>

      {/* Application form */}
      <div className="mt-14 card-vertical rounded-2xl p-8" id="apply">
        {done ? (
          <div className="text-center py-10" data-testid="application-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-2xl font-display font-bold text-white mb-2">Application received</h3>
            <p className="text-slate-400">Thank you for applying to {job.title}. Our team will review your profile and reach out.</p>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-display font-bold text-white mb-6">Apply for this role</h2>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm text-slate-400 mb-1.5 block">Full Name *</label>
                  <input data-testid="apply-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1.5 block">Email *</label>
                  <input data-testid="apply-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50" />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1.5 block">Phone</label>
                <input data-testid="apply-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50" />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1.5 block">Cover Letter</label>
                <textarea data-testid="apply-cover-input" rows={4} value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                  className="w-full bg-[#070b12] border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500/50" />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1.5 block">Resume * (PDF/DOC, max 10MB)</label>
                <label className="flex items-center gap-3 bg-[#070b12] border border-dashed border-slate-700 rounded-lg px-4 py-4 cursor-pointer hover:border-emerald-500/50 transition-colors" data-testid="apply-resume-label">
                  <UploadCloud className="w-5 h-5 text-emerald-400" />
                  <span className="text-slate-300 text-sm">{resume ? resume.name : "Click to upload your resume"}</span>
                  <input data-testid="career-resume-upload" type="file" accept=".pdf,.doc,.docx" className="hidden"
                    onChange={(e) => setResume(e.target.files[0])} />
                </label>
              </div>
              <button type="submit" disabled={submitting} data-testid="career-submit-btn"
                className="btn-emerald px-7 py-3.5 rounded-full disabled:opacity-60">
                {submitting ? "Submitting…" : "Submit Application"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
